import { ContactForm } from '../../components/forms/ContactForm';
import { Button } from '../../components/ui/button';
import { DemoPopup } from '../../components/forms/DemoPopup';
import {
  Code2,
  Globe,
  Smartphone,
  Server,
  Link2,
  CheckCircle2,
} from 'lucide-react';

const services = [
  {
    icon: Globe,
    title: 'Web Applications',
    description: 'Custom web apps built with modern frameworks, responsive design, and best practices.',
  },
  {
    icon: Smartphone,
    title: 'Mobile Apps',
    description: 'Native and cross-platform mobile applications for iOS and Android platforms.',
  },
  {
    icon: Server,
    title: 'API Development',
    description: 'RESTful and GraphQL APIs for seamless data exchange between systems.',
  },
  {
    icon: Link2,
    title: 'System Integration',
    description: 'Connect your existing systems and third-party services for unified operations.',
  },
];

const capabilities = [
  'Full-stack development expertise',
  'Agile development methodology',
  'Dedicated project managers',
  'Comprehensive testing & QA',
  'Post-launch support & maintenance',
  'Transparent communication',
];

export const CustomITPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="custom-it-page">
      {/* Hero Banner */}
      <section className="py-20 md:py-28 hero-glow" data-testid="custom-it-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in-up">
              Custom IT Solutions
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white animate-fade-in-up stagger-1">
              Bespoke Software for Your Business
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mt-6 animate-fade-in-up stagger-2">
              Custom web apps, mobile apps, APIs, and integrations built to your exact specifications.
            </p>
            <div className="mt-8 animate-fade-in-up stagger-3">
              <DemoPopup
                title="Start Your Project"
                formType="Custom IT Inquiry"
                defaultSolution="Custom IT Solutions"
                trigger={
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium glow-primary glow-primary-hover btn-shine"
                    data-testid="custom-it-hero-btn"
                  >
                    Start a Project
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32" data-testid="custom-it-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
              Our Services
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Comprehensive IT solutions tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                className={`glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-${index + 1}`}
                data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mb-6">
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold font-heading text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 md:py-32 bg-secondary/30" data-testid="custom-it-capabilities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Why Work With Us?
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our expert team delivers high-quality custom solutions on time and within budget.
              </p>
              <ul className="space-y-4">
                {capabilities.map((capability) => (
                  <li key={capability} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="animate-fade-in-up stagger-2">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80"
                  alt="Development"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Inquiry Form */}
      <section className="py-24 md:py-32" data-testid="custom-it-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Project Inquiry
              </h2>
              <p className="text-muted-foreground text-lg mt-4">
                Tell us about your project and we'll get back to you with a proposal.
              </p>
            </div>

            <div className="glass rounded-2xl p-8 animate-fade-in-up stagger-1" data-testid="custom-it-form-wrapper">
              <ContactForm
                formType="Custom IT Project Inquiry"
                showInterestedIn={false}
                showBudgetRange={true}
                showProjectRequirements={true}
                interestedInDefault="Custom IT Solutions"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomITPage;
